// Mensualitats overview grid (money view): one row per unitat, chips per
// month of the unitat's 8-month window, inline registration popover.
// All state lives in the Factures context.

import { Fragment } from 'react';

import { useFactures } from './useFacturesContext';
import { FACTURES_UNITATS } from '../../constants/roles';
import {
	addMonthsYm,
	currentYm,
	fmtEUR,
	MENSUAL_WINDOW,
	monthLabel,
	schoolYearMonthsOf,
	shortMonth,
} from './facturesTypes';
import {
	Input,
} from '../Shared/FormStyles';
import {
	Panel,
	SmallButton,
	MensualCell,
	MensualScroll,
	MensualTable,
	QuickRow,
	RowLabel,
	StartCell,
	SubTitle,
	TableHead,
	YearSelect,
} from './Factures.styles';

/** Shared amount input of both popover variants. */
const MensualQuickInput = ({
	amount,
	onChange,
}: {
	amount: string;
	onChange: (v: string) => void;
}) => (
	<Input
		value={amount}
		inputMode="decimal"
		placeholder="150,00"
		style={{ width: '90px' }}
		onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
			onChange(e.target.value)
		}
	/>
);

export const MensualitatsPanel = () => {
	const fac = useFactures();
	const cur = currentYm();

	return (
		<Panel>
			<SubTitle style={{ margin: '0 0 0.75rem 0' }}>Mensualitats</SubTitle>
			<MensualScroll>
				<MensualTable>
					<TableHead style={{ gridColumn: '1 / -1' }}>Unitat</TableHead>
					{FACTURES_UNITATS.map((u) => {
						const ms = fac.summary?.[u.id]?.mensuals ?? {};
						const monthsIn = Object.keys(ms).sort();
						if (monthsIn.length === 0) {
							return (
								<Fragment key={u.id}>
									<RowLabel>{u.nom}</RowLabel>
									<StartCell
										type="button"
										title={`Tria el mes de la 1a mensualitat de ${u.nom}`}
										onClick={() => fac.quickMensualOpen(u.id, null)}
									>
										＋ Primer mes
									</StartCell>
									{fac.quickAdd?.unitat === u.id && (
										<QuickRow key="qa">
											<strong>{u.nom}</strong>
											<YearSelect
												value={fac.quickFirstMonth}
												onChange={(e) =>
													fac.setQuickFirstMonth(e.target.value)
												}
											>
												{schoolYearMonthsOf(fac.year)
													.filter((ym) => ym <= cur)
													.map((ym) => (
														<option key={ym} value={ym}>
															{monthLabel(ym)}
														</option>
													))}
											</YearSelect>
											<MensualQuickInput
												amount={fac.quickAdd.amount}
												onChange={(amount) =>
													fac.setQuickAdd((q) =>
														q ? { ...q, amount } : q
													)
												}
											/>
											<SmallButton
												onClick={() => fac.registerMensual(false)}
												disabled={fac.quickBusy}
											>
												Registra
											</SmallButton>
											<SmallButton
												onClick={() => fac.registerMensual(true)}
												disabled={fac.quickBusy}
											>
												Saltar (0)
											</SmallButton>
											<button className="close" onClick={fac.closeQuickAdd}>
												×
											</button>
										</QuickRow>
									)}
								</Fragment>
							);
						}
						const start = monthsIn[0];
						return (
							<Fragment key={u.id}>
								<RowLabel title={`1a mensualitat: ${monthLabel(start)}`}>
									{u.nom}
								</RowLabel>
								{Array.from(
									{ length: MENSUAL_WINDOW },
									(_, i) => addMonthsYm(start, i)
								).map((ym) => {
									const amt = ms[ym];
									const state:
										| 'paid'
										| 'skipped'
										| 'missing'
										| 'future' =
										amt === undefined
											? ym > cur
												? 'future'
												: 'missing'
											: amt === 0
												? 'skipped'
												: 'paid';
									const clickable = state === 'missing';
									return (
										<MensualCell
											key={ym}
											$state={state}
											$isCurrent={ym === cur}
											type={clickable ? 'button' : undefined}
											disabled={!clickable}
											title={
												state === 'paid'
													? `${monthLabel(ym)} — ${fmtEUR(amt)}`
													: state === 'skipped'
														? `${monthLabel(ym)} — saltada (0)`
														: state === 'future'
															? `${monthLabel(ym)} — encara no es pot registrar`
															: `${monthLabel(ym)} — sense mensualitat; cliques per afegir-la`
											}
											onClick={
												clickable
													? () => fac.quickMensualOpen(u.id, ym)
													: undefined
											}
										>
											<span>{shortMonth(ym)}</span>
											<strong>
												{state === 'paid'
													? `✓ ${amt % 100 === 0 ? `${amt / 100}€` : fmtEUR(amt)}`
													: state === 'skipped'
														? '⏭ 0'
														: state === 'future'
															? '·'
															: '＋'}
											</strong>
										</MensualCell>
									);
								})}
								{fac.quickAdd?.unitat === u.id && (
									<QuickRow key="qa">
										<strong>
											{u.nom.split(' ')[0]} ·{' '}
											{monthLabel(fac.quickAdd.month!)}
										</strong>
										<MensualQuickInput
											amount={fac.quickAdd.amount}
											onChange={(amount) =>
												fac.setQuickAdd((q) => (q ? { ...q, amount } : q))
											}
										/>
										<SmallButton
											onClick={() => fac.registerMensual(false)}
											disabled={fac.quickBusy}
										>
											Registra
										</SmallButton>
										<SmallButton
											onClick={() => fac.registerMensual(true)}
											disabled={fac.quickBusy}
										>
											Saltar (0)
										</SmallButton>
										<button className="close" onClick={fac.closeQuickAdd}>
											×
										</button>
									</QuickRow>
								)}
							</Fragment>
						);
					})}
				</MensualTable>
			</MensualScroll>
		</Panel>
	);
};
